import { timestampDate } from '@bufbuild/protobuf/wkt'

import { ControlTimelineItem } from '@/gen/inventory/v1/control_service_pb'

import { formatTimestamp } from '@/lib/date/date'

import { ControlChangeCard } from '@/pages/control/control-change-card/control-change-card'
import { RiskQuestionnaireThreadCard } from '@/pages/control/risk-questionnaire-thread-card/risk-questionnaire-thread-card'

import { ControlComments } from '@/components/control-comments/control-comments'
import { ActivityEntry, commentDedupeKey } from '@/components/control-comments/types'
import { AssessmentRoomProvider } from '@/components/shared/comment/assessment-room-provider'
import { TenantCommentsProvider } from '@/components/shared/comment/tenant-comments-provider'
import { CommentCard } from '@/components/timeline/comment-card'
import { Timeline } from '@/components/timeline/timeline'
import {
  TimelineItemProps as CustomTimelineItemProps,
  TimelineItem as TimelineItemRow,
} from '@/components/timeline/timeline-item'

type ControlTimelineProps = {
  timelineItems: ControlTimelineItem[]
  controlStatusId: string
  assessmentId?: string
  readOnly?: boolean
  snapshot?: boolean
  onCommentCountChange?: (count: number) => void
}

export const ControlTimeline = ({
  timelineItems,
  controlStatusId,
  assessmentId,
  readOnly,
  snapshot,
  onCommentCountChange,
}: ControlTimelineProps) => {
  const renderedItems = timelineItems || []

  // Find the index of the first (newest) risk questionnaire thread - this is the current ongoing assessment
  const isMostRecentRiskQuestionnaire = renderedItems.findIndex(
    (item) => item.timelineItem.case === 'riskQuestionnaireControlThread',
  )

  const timelineItemProps = renderedItems.map((item, index) =>
    toTimelineItem(controlStatusId, item, index !== isMostRecentRiskQuestionnaire || !!readOnly),
  )

  const serverEntries: ActivityEntry[] = timelineItemProps.map((props, index) => {
    const { timelineItem } = renderedItems[index]
    const time = timelineItem.value?.time
    return {
      key: `server-${index}`,
      timestamp: time,
      dedupeKey:
        timelineItem.case === 'comment' && time ? commentDedupeKey(timestampDate(time)) : undefined,
      node: <TimelineItemRow {...props} />,
    }
  })

  // One Liveblocks client for the whole timeline; the drawer comments and each risk-question
  // card join their own rooms under it (rather than each mounting its own client).
  return (
    <TenantCommentsProvider>
      <div className='flex min-h-0 flex-col'>
        {assessmentId && !snapshot ? (
          <AssessmentRoomProvider assessmentId={assessmentId}>
            <ControlComments
              controlId={controlStatusId}
              assessmentId={assessmentId}
              readOnly={readOnly}
              serverEntries={serverEntries}
              onCommentCountChange={onCommentCountChange}
            />
          </AssessmentRoomProvider>
        ) : (
          <Timeline className='mx-auto w-full' items={timelineItemProps} />
        )}
      </div>
    </TenantCommentsProvider>
  )
}

const toTimelineItem = (
  controlStatusId: string,
  item: ControlTimelineItem,
  readOnly: boolean,
): CustomTimelineItemProps => {
  return {
    userId: item.timelineItem.value?.userId ?? '',
    compactAvatar: item.timelineItem.case === 'riskQuestionnaireControlThread',
    date: formatTimestamp(item.timelineItem.value?.time, 'dateTime'),
    children: <TimelineItem controlStatusId={controlStatusId} item={item} readOnly={readOnly} />,
  }
}

type TimelineItemProps = {
  controlStatusId: string
  item: ControlTimelineItem
  readOnly: boolean
}

const TimelineItem = ({ controlStatusId, item, readOnly }: TimelineItemProps) => {
  const { timelineItem } = item
  switch (timelineItem.case) {
    case 'comment':
      return <CommentCard comment={timelineItem.value} />
    case 'change':
      return <ControlChangeCard change={timelineItem.value} />
    case 'riskQuestionnaireControlThread':
      return (
        <RiskQuestionnaireThreadCard
          controlStatusId={controlStatusId}
          thread={timelineItem.value}
          readOnly={readOnly}
        />
      )
    default:
      return null
  }
}
